import Phaser from 'phaser';
import { COLORS, intToHex } from '../utils/constants';
import { Player } from './Player';
import { Boss } from './Boss';
import { weaponSystem } from '../systems/WeaponSystem';
import { walletManager } from '../systems/WalletManager';

export class HUD {
  private scene: Phaser.Scene;
  private panel: Phaser.GameObjects.Graphics;
  private bossHpBar: Phaser.GameObjects.Graphics;
  private playerHpBar: Phaser.GameObjects.Graphics;
  private cdBar: Phaser.GameObjects.Graphics;

  private bossHpText!: Phaser.GameObjects.Text;
  private phaseText!: Phaser.GameObjects.Text;
  private comboText!: Phaser.GameObjects.Text;
  private scoreText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private challengeRateText!: Phaser.GameObjects.Text;
  private playerHpLabel!: Phaser.GameObjects.Text;
  private weaponText!: Phaser.GameObjects.Text;
  private coinsText!: Phaser.GameObjects.Text;
  private cooldownLabels: Phaser.GameObjects.Text[] = [];
  private cdIconTexts: Phaser.GameObjects.Text[] = [];
  private playerHpFlash = 0;
  private bossHpFlash = 0;

  private score = 0;
  private battleStartTime = 0;
  private bossHpTarget = 0;
  private playerHpTarget = 0;
  private bossHpDisplay = 0;
  private playerHpDisplay = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.panel = scene.add.graphics().setDepth(50).setScrollFactor(0);
    this.bossHpBar = scene.add.graphics().setDepth(51).setScrollFactor(0);
    this.playerHpBar = scene.add.graphics().setDepth(51).setScrollFactor(0);
    this.cdBar = scene.add.graphics().setDepth(51).setScrollFactor(0);
    this.battleStartTime = scene.time.now;
    this.createTexts();
  }

  private createTexts() {
    this.bossHpText = this.scene.add.text(640, 28, 'BOSS', {
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: '12px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(52).setScrollFactor(0);

    this.phaseText = this.scene.add.text(640, 52, '阶段 1 - 学习', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '11px', color: intToHex(COLORS.purple),
    }).setOrigin(0.5).setDepth(52).setScrollFactor(0);

    this.comboText = this.scene.add.text(640, 300, '', {
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: '52px', color: '#00f0ff',
      stroke: intToHex(COLORS.blue), strokeThickness: 4,
    }).setOrigin(0.5).setDepth(52).setAlpha(0).setScrollFactor(0);

    this.scoreText = this.scene.add.text(1230, 20, '分数: 0', {
      fontFamily: 'Arial, sans-serif', fontSize: '18px', color: intToHex(COLORS.cyan), fontStyle: 'bold',
    }).setOrigin(1, 0).setDepth(52).setScrollFactor(0);

    this.timerText = this.scene.add.text(1230, 48, '00:00', {
      fontFamily: 'Arial, monospace', fontSize: '16px', color: COLORS.textDim,
    }).setOrigin(1, 0).setDepth(52).setScrollFactor(0);

    this.challengeRateText = this.scene.add.text(1230, 72, '挑战: --', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '12px', color: COLORS.textDim,
    }).setOrigin(1, 0).setDepth(52).setScrollFactor(0);

    this.playerHpLabel = this.scene.add.text(230, 0, 'HP 100', {
      fontFamily: 'Arial', fontSize: '12px', color: '#ffffff',
    }).setDepth(52).setScrollFactor(0);

    this.weaponText = this.scene.add.text(10, 75, '', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '12px', color: '#ffea00',
    }).setDepth(52).setScrollFactor(0);

    this.coinsText = this.scene.add.text(10, 92, '', {
      fontFamily: 'Arial, sans-serif', fontSize: '11px', color: '#ffea00',
    }).setDepth(52).setScrollFactor(0);

    const cdNames = ['J', 'K', 'L', 'SP', 'SH'];
    cdNames.forEach((l, i) => {
      const t = this.scene.add.text(0, 0, l, {
        fontFamily: 'Arial', fontSize: '10px', color: '#ffffff', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(52).setScrollFactor(0);
      this.cooldownLabels.push(t);
    });

    const cdIcons = ['⚔️', '💥', '✨', '🛡️', '💨'];
    cdIcons.forEach((icon, i) => {
      const t = this.scene.add.text(0, 0, icon, {
        fontFamily: 'Arial, sans-serif', fontSize: '12px',
      }).setOrigin(0.5).setDepth(52).setScrollFactor(0);
      this.cdIconTexts.push(t);
    });
  }

  update(player: Player, boss: Boss, challengeRate: number) {
    this.panel.clear();
    this.bossHpBar.clear();
    this.playerHpBar.clear();
    this.cdBar.clear();

    const w = this.scene.cameras.main.width;
    const h = this.scene.cameras.main.height;

    // Top bar with gradient
    this.panel.fillStyle(0x000000, 0.5);
    this.panel.fillRect(0, 0, w, 70);
    this.panel.lineStyle(1, COLORS.cyan, 0.15);
    this.panel.lineBetween(0, 70, w, 70);

    // Bottom bar
    this.panel.fillStyle(0x000000, 0.4);
    this.panel.fillRect(0, h - 50, w, 50);

    // ── Boss HP ──
    const prevBossHp = this.bossHpDisplay;
    this.bossHpTarget = boss.hp / boss.maxHp;
    this.bossHpDisplay += (this.bossHpTarget - this.bossHpDisplay) * 0.08;
    if (this.bossHpTarget < prevBossHp) this.bossHpFlash = 0.6;
    this.bossHpFlash *= 0.92;

    const bx = w / 2 - 200, by = 12, bw = 400, bbh = 16;
    // Boss HP outer glow
    const bGhost = this.bossHpDisplay > 0.5 ? COLORS.cyan : this.bossHpDisplay > 0.25 ? COLORS.yellow : COLORS.red;
    this.bossHpBar.fillStyle(bGhost, 0.08 + Math.sin(this.scene.time.now * 0.003) * 0.04);
    this.bossHpBar.fillRoundedRect(bx - 4, by - 4, bw + 8, bbh + 8, 6);

    // Background
    this.bossHpBar.fillStyle(0x000000, 0.7);
    this.bossHpBar.fillRoundedRect(bx - 1, by - 1, bw + 2, bbh + 2, 4);
    this.bossHpBar.fillStyle(COLORS.darkGray, 1);
    this.bossHpBar.fillRoundedRect(bx, by, bw, bbh, 3);

    // Damage flash overlay
    if (this.bossHpFlash > 0.01) {
      this.bossHpBar.fillStyle(0xffffff, this.bossHpFlash * 0.15);
      this.bossHpBar.fillRoundedRect(bx, by, bw, bbh, 3);
    }

    // Segmented HP fill
    const bCol = this.bossHpDisplay > 0.5 ? COLORS.cyan : this.bossHpDisplay > 0.25 ? COLORS.yellow : COLORS.red;
    if (this.bossHpDisplay > 0.01) {
      const segs = 20;
      const segW = (bw * this.bossHpDisplay) / segs;
      for (let i = 0; i < segs; i++) {
        const sx = bx + i * segW;
        const gap = i > 0 ? 1 : 0;
        this.bossHpBar.fillStyle(bCol, 1);
        this.bossHpBar.fillRect(sx + gap, by + 2, segW - gap, bbh - 4);
        this.bossHpBar.fillStyle(0xffffff, 0.12 + (i % 2) * 0.06);
        this.bossHpBar.fillRect(sx + gap, by + 2, segW - gap, 3);
      }
    }

    this.bossHpText.setText(`BOSS  ${Math.ceil(boss.hp)}/${boss.maxHp}`);

    const phaseNames = ['', '阶段 I — 学习', '阶段 II — 适应', '阶段 III — 狂暴'];
    const phaseColors = ['', intToHex(COLORS.purple), intToHex(COLORS.yellow), intToHex(COLORS.red)];
    this.phaseText.setText(phaseNames[boss.phase]).setColor(phaseColors[boss.phase]);

    // ── Player HP ──
    const prevPlayerHp = this.playerHpDisplay;
    this.playerHpTarget = player.hp / player.maxHp;
    this.playerHpDisplay += (this.playerHpTarget - this.playerHpDisplay) * 0.08;
    if (this.playerHpTarget < prevPlayerHp) this.playerHpFlash = 0.5;
    this.playerHpFlash *= 0.9;

    const px = 20, py = h - 37, pw = 200, phh = 18;

    // Player HP outer glow
    const pGhost = player.hp > 60 ? COLORS.green : player.hp > 30 ? COLORS.yellow : COLORS.red;
    this.playerHpBar.fillStyle(pGhost, 0.06 + Math.sin(this.scene.time.now * 0.004) * 0.03);
    this.playerHpBar.fillRoundedRect(px - 3, py - 3, pw + 6, phh + 6, 5);

    // Background
    this.playerHpBar.fillStyle(0x000000, 0.7);
    this.playerHpBar.fillRoundedRect(px - 1, py - 1, pw + 2, phh + 2, 4);
    this.playerHpBar.fillStyle(COLORS.darkGray, 1);
    this.playerHpBar.fillRoundedRect(px, py, pw, phh, 3);

    // Damage flash overlay
    if (this.playerHpFlash > 0.01) {
      this.playerHpBar.fillStyle(0xffffff, this.playerHpFlash * 0.12);
      this.playerHpBar.fillRoundedRect(px, py, pw, phh, 3);
    }

    // Segmented HP fill
    const pCol = player.hp > 60 ? COLORS.green : player.hp > 30 ? COLORS.yellow : COLORS.red;
    if (this.playerHpDisplay > 0.01) {
      const segs = 10;
      const segW = (pw * this.playerHpDisplay) / segs;
      for (let i = 0; i < segs; i++) {
        const sx = px + i * segW;
        const gap = i > 0 ? 1 : 0;
        this.playerHpBar.fillStyle(pCol, 1);
        this.playerHpBar.fillRect(sx + gap, py + 2, segW - gap, phh - 4);
        this.playerHpBar.fillStyle(0xffffff, 0.1 + (i % 2) * 0.05);
        this.playerHpBar.fillRect(sx + gap, py + 2, segW - gap, 3);
      }
    }

    // Low HP warning pulsating border
    if (player.hp <= 30) {
      const pulse = 0.4 + Math.sin(this.scene.time.now * 0.008) * 0.3;
      this.playerHpBar.lineStyle(2, COLORS.red, pulse);
      this.playerHpBar.strokeRoundedRect(px - 3, py - 3, pw + 6, phh + 6, 5);
    }

    this.playerHpLabel.setPosition(px + pw + 12, py + 1);
    this.playerHpLabel.setText(`HP ${Math.ceil(player.hp)}`);

    // ── Cooldowns ──
    const cdx = [20, 60, 100, 145, 185], cdy = h - 62;
    const prog = [
      player.getCooldownProgress('attack'), player.getCooldownProgress('heavy'),
      player.getCooldownProgress('skill'), player.getCooldownProgress('shield'),
      player.getCooldownProgress('dash'),
    ];
    const cols = [COLORS.cyan, COLORS.yellow, COLORS.purple, COLORS.green, COLORS.blue];
    const cdNames = ['J', 'K', 'L', 'SP', 'SH'];
    const cdIcons = ['⚔️', '💥', '✨', '🛡️', '💨'];

    for (let i = 0; i < 5; i++) {
      this.drawCD(cdx[i], cdy, prog[i], cols[i], cdIcons[i]);
      this.cooldownLabels[i].setPosition(cdx[i] + 18, cdy + 26);
      this.cooldownLabels[i].setColor(prog[i] >= 1 ? '#ffffff' : COLORS.textDim);
      this.cooldownLabels[i].setText(cdNames[i]);
      this.cdIconTexts[i].setPosition(cdx[i] + 18, cdy + 8);
      this.cdIconTexts[i].setAlpha(prog[i] >= 1 ? 1 : 0.5);
    }

    // Shield active
    if (player.shieldActive) {
      const pulse = 0.3 + Math.sin(this.scene.time.now * 0.006) * 0.3;
      this.cdBar.fillStyle(COLORS.cyan, 0.2);
      this.cdBar.fillRect(px, py - 10, pw, 6);
      this.cdBar.lineStyle(1, COLORS.cyan, pulse);
      this.cdBar.strokeRect(px, py - 10, pw, 6);
    }

    // Combo
    if (player.stats.combo > 1) {
      const c = player.stats.combo;
      this.comboText.setText(`${c}x 连击`);
      this.comboText.setAlpha(0.8 + Math.sin(this.scene.time.now * 0.01) * 0.2);
      this.comboText.setScale(1 + Math.min(c * 0.015, 0.3));
      const comboCol = c >= 20 ? '#ffea00' : c >= 10 ? '#ff9100' : '#00f0ff';
      this.comboText.setColor(comboCol);
    } else {
      this.comboText.setAlpha(0).setScale(1);
    }

    this.scoreText.setText(`分数: ${this.score}`);

    const elapsed = Math.floor((this.scene.time.now - this.battleStartTime) / 1000);
    this.timerText.setText(`${String(Math.floor(elapsed / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`);

    if (challengeRate > 0) this.challengeRateText.setText(`挑战: ${Math.round(challengeRate * 100)}%`);

    const equipped = weaponSystem.getEquipped();
    this.weaponText.setText(`武器: ${equipped.icon} ${equipped.name} [Q/E切换]`);
    this.coinsText.setText(`💰 ${walletManager.getCoins()}`);
  }

  private drawCD(x: number, y: number, progress: number, color: number, icon: string) {
    const s = 36;
    // Outer glow
    if (progress >= 1) {
      const glow = 0.15 + Math.sin(this.scene.time.now * 0.006) * 0.12;
      this.cdBar.fillStyle(color, glow);
      this.cdBar.fillRoundedRect(x - 3, y - 3, s + 6, s + 6, 7);
    }

    // BG
    this.cdBar.fillStyle(0x000000, 0.75);
    this.cdBar.fillRoundedRect(x, y, s, s, 5);

    if (progress < 1) {
      // Sweep fill from bottom with gradient effect
      const fillH = s * progress;
      // Darker fill base
      this.cdBar.fillStyle(color, 0.2);
      this.cdBar.fillRoundedRect(x + 2, y + 2 + s - fillH, s - 4, fillH - 2, 3);
      // Bright fill - smaller
      this.cdBar.fillStyle(color, 0.4);
      this.cdBar.fillRect(x + 3, y + 2 + s - fillH, s - 6, fillH - 4);
      // Animated sweep line
      const sweepY = y + 2 + s - fillH;
      this.cdBar.fillStyle(color, 0.6 + Math.sin(this.scene.time.now * 0.01) * 0.2);
      this.cdBar.fillRect(x + 3, sweepY, s - 6, 2);

      // Border dim
      this.cdBar.lineStyle(1, color, 0.3);
      this.cdBar.strokeRoundedRect(x, y, s, s, 5);
    } else {
      // Ready - pulsing glow border
      this.cdBar.fillStyle(color, 0.1);
      this.cdBar.fillRoundedRect(x + 2, y + 2, s - 4, s - 4, 4);
      const pulse = 0.5 + Math.sin(this.scene.time.now * 0.006) * 0.4;
      this.cdBar.lineStyle(2, color, pulse);
      this.cdBar.strokeRoundedRect(x - 1, y - 1, s + 2, s + 2, 6);
    }
  }

  addScore(a: number) { this.score += a; }
  getScore(): number { return this.score; }
  getBattleTime(): number { return (this.scene.time.now - this.battleStartTime) / 1000; }

  showFloatingText(x: number, y: number, text: string, color = '#00f0ff') {
    const ft = this.scene.add.text(x, y - 20, text, {
      fontFamily: 'Arial', fontSize: '16px', color, fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(55).setScrollFactor(0);
    this.scene.tweens.add({ targets: ft, y: y - 60, alpha: 0, duration: 800, onComplete: () => ft.destroy() });
  }

  destroy() {
    this.panel.destroy();
    this.bossHpBar.destroy();
    this.playerHpBar.destroy();
    this.cdBar.destroy();
    this.bossHpText.destroy();
    this.phaseText.destroy();
    this.comboText.destroy();
    this.scoreText.destroy();
    this.timerText.destroy();
    this.challengeRateText.destroy();
    this.playerHpLabel.destroy();
    this.weaponText.destroy();
    this.coinsText.destroy();
    this.cooldownLabels.forEach(l => l.destroy());
    this.cdIconTexts.forEach(l => l.destroy());
  }
}
